import InvitationSoundtrackSettings from "./InvitationSoundtrackSettings";
import KandyanStorySettings from "./KandyanStorySettings";
import { useState, lazy, Suspense } from "react";
import type { InvitationConfig } from "../../types/invitation";
import type { RegistryEvent } from "../../types/registry";
import InvitationPhotos from "./InvitationPhotos";
import { newInvitation } from "../../lib/invitation";
const Preview = lazy(() =>
  import("./AboveTheClouds").then((m) => ({ default: m.InvitationPreview })),
);
interface Props {
  value?: InvitationConfig;
  onChange: (value: InvitationConfig | undefined) => void;
  registry: { id: string; title: string; event_date: string };
  events: RegistryEvent[];
  previewEvents?: RegistryEvent[];
  giftRegistryAvailable: boolean;
  onUploadStateChange?: (busy: boolean) => void;
}
export default function InvitationSettings({
  value,
  onChange,
  registry,
  events,
  previewEvents,
  giftRegistryAvailable,
  onUploadStateChange,
}: Props) {
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const update = (patch: Partial<InvitationConfig>) =>
    value && onChange({ ...value, ...patch });
  return (
    <section
      className="bg-white rounded-xl p-6 border border-rose-100 text-gray-800 space-y-5"
      aria-label="Invitation design settings"
    >
      <fieldset disabled={uploading} className="space-y-5">
        <div>
          <h2 className="text-xl font-semibold">Invitation design</h2>
          <p className="text-sm text-gray-600">
            Choose how guests see the function details before RSVP.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <button
            type="button"
            aria-pressed={!value}
            onClick={() => onChange(undefined)}
            className={`p-5 border-2 rounded-lg text-left ${!value ? "border-rose-400" : "border-gray-200"}`}
          >
            <strong>Classic invitation</strong>
            <p className="text-sm mt-2">Your current theme and RSVP layout.</p>
          </button>
          <button
            type="button"
            aria-pressed={value?.template === "above-the-clouds"}
            onClick={() => {
              onChange(
                value
                  ? { ...value, template: "above-the-clouds" }
                  : newInvitation(),
              );
            }}
            className={`border-2 rounded-lg overflow-hidden text-left ${value?.template === "above-the-clouds" ? "border-rose-400" : "border-gray-200"}`}
          >
            <img
              src="/invitations/above-the-clouds/cloud-terrace.png"
              alt="Above the Clouds invitation"
              className="w-full h-28 object-cover"
              loading="lazy"
            />
            <div className="p-3">
              <strong>Above the Clouds</strong>
              <p className="text-sm">
                A romantic entrance, function details and timeline.
              </p>
            </div>
          </button>
          <button
            type="button"
            aria-pressed={value?.template === "timeless-ivory"}
            onClick={() =>
              onChange(
                value
                  ? { ...value, template: "timeless-ivory" }
                  : newInvitation("timeless-ivory"),
              )
            }
            className={`border-2 rounded-lg overflow-hidden text-left ${value?.template === "timeless-ivory" ? "border-rose-400" : "border-gray-200"}`}
          >
            <div
              className="h-28 bg-[#faf7f0] text-[#38372f] flex flex-col items-center justify-center gap-2"
              aria-hidden="true"
            >
              <span className="text-[#97876a] text-xl">❧</span>
              <span className="font-serif text-3xl">
                You <em className="text-[#97876a]">&</em> Me
              </span>
              <span className="tracking-[0.25em] text-[9px]">
                TIMELESS IVORY
              </span>
            </div>
            <div className="p-3">
              <strong>Timeless Ivory</strong>
              <p className="text-sm">
                Warm ivory, personal photos and an elegant function timeline.
              </p>
            </div>
          </button>
          <button type="button" aria-pressed={value?.template === "midnight-garden"}
            onClick={() => onChange(value ? { ...value, template: "midnight-garden" } : newInvitation("midnight-garden"))}
            className={`border-2 rounded-lg overflow-hidden text-left ${value?.template === "midnight-garden" ? "border-rose-400" : "border-gray-200"}`}>
            <div className="h-28 bg-[#102b25] text-[#ecd5a5] flex flex-col items-center justify-center gap-2 relative overflow-hidden">
              <img src="/invitations/midnight-garden/jasmine-corner.png" alt="" className="absolute left-0 top-0 h-full w-24 object-cover opacity-70" />
              <span className="font-serif text-3xl relative">You & Me</span><span className="tracking-[0.25em] text-[9px] relative">MIDNIGHT GARDEN</span>
            </div>
            <div className="p-3"><strong>Midnight Garden</strong><p className="text-sm">Opening garden doors, champagne gold and a glowing evening timeline.</p></div>
          </button>
          <button type="button" aria-pressed={value?.template === "champagne-reverie"}
            onClick={() => onChange(value ? { ...value, template: "champagne-reverie" } : newInvitation("champagne-reverie"))}
            className={`border-2 rounded-lg overflow-hidden text-left ${value?.template === "champagne-reverie" ? "border-rose-400" : "border-gray-200"}`}>
            <div className="h-28 bg-[#fffdf8] text-[#514940] flex flex-col items-center justify-center gap-2 relative overflow-hidden">
              <img src="/invitations/champagne-reverie/satin-panel.png" alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60" />
              <span className="font-serif text-3xl relative">You & Me</span><span className="tracking-[0.2em] text-[9px] relative">CHAMPAGNE REVERIE</span>
            </div>
            <div className="p-3"><strong>Champagne Reverie</strong><p className="text-sm">Luminous satin curtains, pearl tones and an elegant celebration timeline.</p></div>
          </button>
          <button type="button" aria-pressed={value?.template === "tuscan-sunset"}
            onClick={() => onChange(value ? { ...value, template: "tuscan-sunset" } : newInvitation("tuscan-sunset"))}
            className={`border-2 rounded-lg overflow-hidden text-left ${value?.template === "tuscan-sunset" ? "border-rose-400" : "border-gray-200"}`}>
            <div className="h-28 bg-[#fff9ed] text-[#a54927] flex flex-col items-center justify-center gap-2 relative overflow-hidden">
              <img src="/invitations/tuscan-sunset/olive-plaster.png" alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60" />
              <span className="font-serif text-3xl relative">You & Me</span><span className="tracking-[0.2em] text-[9px] relative">TUSCAN SUNSET</span>
            </div>
            <div className="p-3"><strong>Tuscan Sunset</strong><p className="text-sm">Terracotta arches, olive details and a golden celebration timeline.</p></div>
          </button>
          <button type="button" aria-pressed={value?.template === "celestial-vows"}
            onClick={() => onChange(value ? { ...value, template: "celestial-vows" } : newInvitation("celestial-vows"))}
            className={`border-2 rounded-lg overflow-hidden text-left ${value?.template === "celestial-vows" ? "border-rose-400" : "border-gray-200"}`}>
            <div className="h-28 bg-[#101a30] text-[#f4f3ee] flex flex-col items-center justify-center gap-2 relative overflow-hidden">
              <img src="/invitations/celestial-vows/moonlit-terrace.png" alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60" />
              <span className="font-serif text-3xl relative">☾ You & Me</span><span className="tracking-[0.2em] text-[9px] relative">CELESTIAL VOWS</span>
            </div>
            <div className="p-3"><strong>Celestial Vows</strong><p className="text-sm">A moonlight reveal, pearl details and a constellation timeline.</p></div>
          </button>
          <button type="button" aria-pressed={value?.template === "royal-kandyan-arrival"}
            onClick={() => onChange(value ? { ...value, template: "royal-kandyan-arrival" } : newInvitation("royal-kandyan-arrival"))}
            className={`border-2 rounded-lg overflow-hidden text-left ${value?.template === "royal-kandyan-arrival" ? "border-rose-400" : "border-gray-200"}`}>
            <div className="h-28 bg-[#702e38] text-[#fff9ef] flex flex-col items-center justify-center gap-2 relative overflow-hidden">
              <img src="/invitations/royal-kandyan-arrival/car-arrival.png" alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60" />
              <span className="font-serif text-3xl relative">You & Me</span><span className="tracking-[0.2em] text-[9px] relative">ROYAL KANDYAN ARRIVAL</span>
            </div>
            <div className="p-3"><strong>Royal Kandyan Arrival</strong><p className="text-sm">An illustrated vintage-car arrival, ivory petals and Kandyan tradition.</p></div>
          </button>
          <button type="button" aria-pressed={value?.template === "lotus-poruwa"}
            onClick={() => onChange(value ? { ...value, template: "lotus-poruwa" } : newInvitation("lotus-poruwa"))}
            className={`border-2 rounded-lg overflow-hidden text-left ${value?.template === "lotus-poruwa" ? "border-rose-400" : "border-gray-200"}`}>
            <div className="h-28 bg-[#9b7941] text-[#fff9ef] flex flex-col items-center justify-center gap-2 relative overflow-hidden">
              <img src="/invitations/lotus-poruwa/poruwa-arrival.png" alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60" />
              <span className="font-serif text-3xl relative">You & Me</span><span className="tracking-[0.2em] text-[9px] relative">LOTUS PORUWA</span>
            </div>
            <div className="p-3"><strong>Lotus Poruwa</strong><p className="text-sm">A jasmine-draped Poruwa, pink lotus petals and luminous ivory.</p></div>
          </button>
          <button type="button" aria-pressed={value?.template === "araliya-courtyard"}
            onClick={() => onChange(value ? { ...value, template: "araliya-courtyard" } : newInvitation("araliya-courtyard"))}
            className={`border-2 rounded-lg overflow-hidden text-left ${value?.template === "araliya-courtyard" ? "border-rose-400" : "border-gray-200"}`}>
            <div className="h-28 bg-[#667052] text-[#fff9ef] flex flex-col items-center justify-center gap-2 relative overflow-hidden">
              <img src="/invitations/araliya-courtyard/courtyard-couple.png" alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60" />
              <span className="font-serif text-3xl relative">You & Me</span><span className="tracking-[0.2em] text-[9px] relative">ARALIYA COURTYARD</span>
            </div>
            <div className="p-3"><strong>Araliya Courtyard</strong><p className="text-sm">Carved doors, araliya blossoms and an ivory-and-sage courtyard.</p></div>
          </button>
          <button type="button" aria-pressed={value?.template === "ceylon-heritage"}
            onClick={() => onChange(value ? { ...value, template: "ceylon-heritage" } : newInvitation("ceylon-heritage"))}
            className={`border-2 rounded-lg overflow-hidden text-left ${value?.template === "ceylon-heritage" ? "border-rose-400" : "border-gray-200"}`}>
            <div className="h-28 bg-[#681f22] text-[#fff9ef] flex flex-col items-center justify-center gap-2 relative overflow-hidden">
              <img src="/invitations/ceylon-heritage/heritage-couple.png" alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60" />
              <span className="font-serif text-3xl relative">You & Me</span><span className="tracking-[0.2em] text-[9px] relative">CEYLON HERITAGE</span>
            </div>
            <div className="p-3"><strong>Ceylon Heritage</strong><p className="text-sm">Burgundy and gold panels, a warm lamp glow and a heritage celebration.</p></div>
          </button>
        </div>
        {value && (
          <>
            <InvitationSoundtrackSettings value={value} update={update} onBusyChange={busy=>{setUploading(busy);onUploadStateChange?.(busy);}} />
            <InvitationPhotos
              value={value}
              onChange={update}
              onBusyChange={(busy) => {
                setUploading(busy);
                onUploadStateChange?.(busy);
              }}
            />
            <div className="grid sm:grid-cols-2 gap-4">
              {(["firstName", "secondName"] as const).map((key, i) => (
                <label key={key} className="text-sm">
                  {i === 0 ? "First name" : "Second name"}
                  <input
                    value={value[key]}
                    maxLength={60}
                    onChange={(e) => update({ [key]: e.target.value })}
                    className="block w-full border rounded p-2 mt-1"
                  />
                </label>
              ))}
            </div>
            <label className="block text-sm">
              Invitation message
              <textarea
                value={value.message}
                maxLength={1000}
                rows={3}
                onChange={(e) => update({ message: e.target.value })}
                className="block w-full border rounded p-2 mt-1"
              />
            </label>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="text-sm">
                RSVP deadline (optional)
                <input
                  type="date"
                  value={value.rsvpDeadline}
                  max={registry.event_date || undefined}
                  onChange={(e) => update({ rsvpDeadline: e.target.value })}
                  className="block w-full border rounded p-2 mt-1"
                />
              </label>
              {value.template === "above-the-clouds" && (
                <label className="text-sm">
                  Colour story
                  <select
                    value={value.palette}
                    onChange={(e) =>
                      update({ palette: e.target.value as "rose" | "mauve" })
                    }
                    className="block w-full border rounded p-2 mt-1"
                  >
                    <option value="rose">Dusty rose</option>
                    <option value="mauve">Soft mauve</option>
                  </select>
                </label>
              )}
            </div>
            <label className="flex gap-2 items-center text-sm">
              <input
                type="checkbox"
                checked={value.showGiftRegistry}
                onChange={(e) => update({ showGiftRegistry: e.target.checked })}
              />{" "}
              Open the gift registry after RSVP and show the skip button
            </label>
            <p className="text-xs text-gray-500">
              Shown only when the registry is open and has gifts. Otherwise
              guests finish with a thank-you message.
            </p>
            <label className="flex gap-2 items-center text-sm">
              <input
                type="checkbox"
                checked={value.gentleMotion}
                onChange={(e) => update({ gentleMotion: e.target.checked })}
              />{" "}
              Use gentle motion only
            </label>
            <div>
              {(value.template === "royal-kandyan-arrival" || value.template === "lotus-poruwa" || value.template === "araliya-courtyard" || value.template === "ceylon-heritage") && <KandyanStorySettings value={value} update={update} />}
              <h3 className="font-medium">Public function timeline</h3>
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to use your event schedule. These entries are
                visible to guests; private planning notes are never imported.
              </p>
            </div>
            {value.timeline.map((entry, i) => (
              <div className="border rounded p-3 space-y-2" key={entry.id}>
                <label className="block text-xs">
                  Link to a function
                  <select
                    aria-label={`Timeline function ${i + 1}`}
                    value={entry.eventId || ""}
                    onChange={(e) =>
                      update({
                        timeline: value.timeline.map((t) =>
                          t.id === entry.id
                            ? { ...t, eventId: e.target.value || undefined }
                            : t,
                        ),
                      })
                    }
                    className="block border rounded p-2 w-full"
                  >
                    <option value="">Custom activity</option>
                    {events.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.name}
                      </option>
                    ))}
                  </select>
                </label>
                {!entry.eventId && (
                  <div className="grid sm:grid-cols-3 gap-2">
                    <input
                      aria-label={`Activity ${i + 1}`}
                      placeholder="e.g. First dance"
                      maxLength={100}
                      value={entry.title}
                      onChange={(e) =>
                        update({
                          timeline: value.timeline.map((t) =>
                            t.id === entry.id
                              ? { ...t, title: e.target.value }
                              : t,
                          ),
                        })
                      }
                      className="border rounded p-2 min-w-0"
                    />
                    <input
                      aria-label={`Time ${i + 1}`}
                      type="time"
                      value={entry.time}
                      onChange={(e) =>
                        update({
                          timeline: value.timeline.map((t) =>
                            t.id === entry.id
                              ? { ...t, time: e.target.value }
                              : t,
                          ),
                        })
                      }
                      className="border rounded p-2 min-w-0"
                    />
                    <input
                      aria-label={`Date ${i + 1}`}
                      type="date"
                      value={entry.date || registry.event_date}
                      onChange={(e) =>
                        update({
                          timeline: value.timeline.map((t) =>
                            t.id === entry.id
                              ? { ...t, date: e.target.value }
                              : t,
                          ),
                        })
                      }
                      className="border rounded p-2 min-w-0"
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() =>
                    update({
                      timeline: value.timeline.filter((t) => t.id !== entry.id),
                    })
                  }
                  className="text-sm text-rose-700"
                >
                  Remove activity
                </button>
              </div>
            ))}
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                disabled={value.timeline.length >= 20}
                className="border rounded px-4 py-2 disabled:opacity-40"
                onClick={() =>
                  update({
                    timeline: [
                      ...value.timeline,
                      {
                        id: crypto.randomUUID(),
                        title: "New activity",
                        time: "15:00",
                        date: registry.event_date,
                      },
                    ],
                  })
                }
              >
                Add activity
              </button>
              <button
                type="button"
                onClick={() => setPreview(true)}
                className="bg-rose-700 text-white rounded px-4 py-2"
              >
                Preview as guest
              </button>
            </div>
            {preview && (
              <Suspense fallback={<p>Loading preview…</p>}>
                <Preview
                  registry={registry}
                  events={previewEvents || events}
                  config={value}
                  giftRegistryAvailable={
                    value.showGiftRegistry && giftRegistryAvailable
                  }
                  onClose={() => setPreview(false)}
                />
              </Suspense>
            )}
          </>
        )}
      </fieldset>
    </section>
  );
}
